import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import SpotServices from "../../services/externalServices/TourPackageServices/spotServices";
import {
  Grid,
  TextField,
  MenuItem,
  Button,
  Checkbox,
  FormControlLabel,
  Typography,
  Box,
  Snackbar,
  Alert,
} from "@mui/material";

const EditSpot = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [originalSpot, setOriginalSpot] = useState(null);
  const [spot, setSpot] = useState({
    name: "",
    audioGuide: {},
    location: { latitude: 0, longitude: 0 },
    description: "",
    thumbnailImage: "",
    isEnabled: true,
    internalComments: "",
  });
  const [selectedLanguage, setSelectedLanguage] = useState("en");
  const [availableLanguages] = useState(["en", "kn"]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success", // 'success' | 'error' | 'warning' | 'info'
  });

  useEffect(() => {
    const fetchSpot = async () => {
      try {
        const response = await SpotServices.getSpotsById(id);
        setOriginalSpot(response.data || response);

        // Determine available languages from the spot data
        const languages = Object.keys(response.data?.name || { en: "" });
        // setAvailableLanguages(languages);

        // Set initial language to the first available one
        if (languages.length > 0) {
          setSelectedLanguage(languages[0]);
        }

        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };
    fetchSpot();
  }, [id]);

  useEffect(() => {
    if (originalSpot) {
      updateFormWithLanguage(selectedLanguage);
    }
  }, [originalSpot, selectedLanguage]);

  const updateFormWithLanguage = (lang) => {
    if (!originalSpot) return;

    setSpot({
      name: originalSpot.name[lang] || "",
      audioGuide: originalSpot.audioGuide[lang] || { isEnabled: false },
      location: originalSpot.location,
      description: originalSpot.description,
      thumbnailImage: originalSpot.thumbnailImage,
      isEnabled: originalSpot.isEnabled,
      internalComments: originalSpot.internalComments || "",
    });
  };

  const handleLanguageChange = (e) => {
    setSelectedLanguage(e.target.value);
  };

  const handleSnackbarClose = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSpot((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleLocationChange = (field, value) => {
    setSpot((prev) => ({
      ...prev,
      location: {
        ...prev.location,
        [field]: value,
      },
    }));
  };

  const handleAudioGuideChange = (field, value) => {
    setSpot((prev) => ({
      ...prev,
      audioGuide: {
        ...prev.audioGuide,
        [field]: value,
      },
    }));
  };

  const handleAudioUrlChange = (duration, field, value) => {
    setSpot((prev) => ({
      ...prev,
      audioGuide: {
        ...prev.audioGuide,
        [duration]: {
          ...prev.audioGuide[duration],
          [field]: value,
        },
      },
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const updatedSpot = {
        ...originalSpot,
        name: {
          ...originalSpot.name,
          [selectedLanguage]: spot.name,
        },
        audioGuide: {
          ...originalSpot.audioGuide,
          [selectedLanguage]: spot.audioGuide,
        },
        description: spot.description,
        thumbnailImage: spot.thumbnailImage,
        location: spot.location,
        isEnabled: spot.isEnabled,
        internalComments: spot.internalComments || "",
      };

      await SpotServices.updateSpots(id, updatedSpot);
      setSnackbar({
        open: true,
        message: "Spot updated successfully!",
        severity: "success",
      });
      //   navigate('/spots');
    } catch (err) {
      setError(err.message);
      setSnackbar({
        open: true,
        message: "Failed to update spot: " + err.message,
        severity: "error",
      });
    }
  };

  if (loading) return <Typography>Loading...</Typography>;
  if (error) return <Typography color="error">Error: {error}</Typography>;
  if (!originalSpot) return <Typography>Spot not found</Typography>;

  return (
    <Box sx={{ maxWidth: 800, margin: "auto", p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Edit Spot
      </Typography>

      <form onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          {/* Language Selector Dropdown */}
          <Grid item xs={12}>
            <TextField
              select
              label="Select Language"
              value={selectedLanguage}
              onChange={handleLanguageChange}
              fullWidth
              variant="outlined"
            >
              {availableLanguages.map((lang) => (
                <MenuItem key={lang} value={lang}>
                  {lang === "en" ? "English" : "Kannada"} ({lang.toUpperCase()})
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {/* Basic Fields */}
          <Grid item xs={12}>
            <TextField
              label="Name"
              value={spot.name}
              onChange={(e) =>
                handleChange({
                  target: { name: "name", value: e.target.value },
                })
              }
              fullWidth
              variant="outlined"
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Description"
              value={spot.description}
              onChange={(e) =>
                handleChange({
                  target: { name: "description", value: e.target.value },
                })
              }
              fullWidth
              multiline
              rows={4}
              variant="outlined"
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Thumbnail Image URL"
              value={spot.thumbnailImage}
              onChange={(e) =>
                handleChange({
                  target: { name: "thumbnailImage", value: e.target.value },
                })
              }
              fullWidth
              variant="outlined"
            />
            {spot.thumbnailImage && (
              <Box mt={2}>
                <img
                  src={spot.thumbnailImage}
                  alt="Preview"
                  style={{ maxHeight: 200, maxWidth: "100%" }}
                  onError={(e) => (e.target.style.display = "none")}
                />
              </Box>
            )}
          </Grid>

          {/* Location Fields */}
          <Grid item xs={6}>
            <TextField
              label="Latitude"
              type="number"
              value={spot.location.latitude}
              onChange={(e) =>
                handleLocationChange("latitude", parseFloat(e.target.value))
              }
              fullWidth
              variant="outlined"
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Longitude"
              type="number"
              value={spot.location.longitude}
              onChange={(e) =>
                handleLocationChange("longitude", parseFloat(e.target.value))
              }
              fullWidth
              variant="outlined"
            />
          </Grid>

          {/* Audio Guide Section */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Audio Guide ({selectedLanguage.toUpperCase()})
            </Typography>

            <FormControlLabel
              control={
                <Checkbox
                  checked={spot.audioGuide?.isEnabled || false}
                  onChange={(e) =>
                    handleAudioGuideChange("isEnabled", e.target.checked)
                  }
                />
              }
              label="Enable Audio Guide"
            />

            {spot.audioGuide?.isEnabled && (
              <Box
                mt={2}
                sx={{ border: "1px solid #eee", p: 2, borderRadius: 1 }}
              >
                <TextField
                  label="Guide Name"
                  value={spot.audioGuide?.name || ""}
                  onChange={(e) =>
                    handleAudioGuideChange("name", e.target.value)
                  }
                  fullWidth
                  variant="outlined"
                  sx={{ mb: 2 }}
                />

                {["short", "medium", "long"].map((duration) => (
                  <Box key={duration} mb={3}>
                    <Typography variant="subtitle1" gutterBottom>
                      {duration.charAt(0).toUpperCase() + duration.slice(1)}{" "}
                      Version
                    </Typography>
                    <TextField
                      label="Audio URL"
                      value={spot.audioGuide?.[duration]?.audioUrl || ""}
                      onChange={(e) =>
                        handleAudioUrlChange(
                          duration,
                          "audioUrl",
                          e.target.value
                        )
                      }
                      fullWidth
                      variant="outlined"
                      sx={{ mb: 2 }}
                    />
                    <TextField
                      label="Subtitles URL"
                      value={spot.audioGuide?.[duration]?.subtitlesUrl || ""}
                      onChange={(e) =>
                        handleAudioUrlChange(
                          duration,
                          "subtitlesUrl",
                          e.target.value
                        )
                      }
                      fullWidth
                      variant="outlined"
                    />
                  </Box>
                ))}
              </Box>
            )}
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="internalComments"
              value={spot.internalComments}
              onChange={(e) =>
                handleChange({
                  target: { name: "internalComments", value: e.target.value },
                })
              }
              fullWidth
              multiline
              rows={4}
              variant="outlined"
            />
          </Grid>

          {/* Status and Actions */}
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={spot.isEnabled}
                  onChange={(e) =>
                    handleChange({
                      target: { name: "isEnabled", value: e.target.checked },
                    })
                  }
                />
              }
              label="Spot Enabled"
            />
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
              <Button variant="outlined" onClick={() => navigate("/spots")}>
                Cancel
              </Button>
              <Button type="submit" variant="contained" color="primary">
                Save Changes
              </Button>
            </Box>
          </Grid>
        </Grid>
      </form>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EditSpot;
